import React, { useState, useEffect, useRef } from "react";
import { Upload, Image as ImageIcon, Loader2, Check, X, Clipboard, Maximize2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { uploadFile } from "@/lib/uploadFile";
import { getUploadUrl, validateUploadFile, MAX_ATTACHMENT_SIZE, extractErrorMessage } from "@/lib/utils";
import { trpcClient } from "@/lib/trpc";

interface QuickUploadLayoutImageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  layout: {
    id: string;
    name: string;
    layoutCode?: string | null;
    fileUrl?: string | null;
    sheetSize?: string | null;
    ups?: number | null;
  } | null;
  onSuccess: () => void;
}

export function QuickUploadLayoutImageDialog({
  open,
  onOpenChange,
  layout,
  onSuccess,
}: QuickUploadLayoutImageDialogProps) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (layout) {
      setFileUrl(layout.fileUrl || null);
    } else {
      setFileUrl(null);
    }
  }, [layout, open]);

  // Handle Clipboard Paste (Ctrl+V / Cmd+V)
  useEffect(() => {
    if (!open) return;

    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            await processUpload(file);
            break;
          }
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [open]);

  const processUpload = async (file: File) => {
    try {
      validateUploadFile(file, {
        maxSize: MAX_ATTACHMENT_SIZE,
        label: "Ảnh bài bình",
      });
      setIsUploading(true);
      const res = await uploadFile(file);
      setFileUrl(res.url);
      toast.success("Đã tải ảnh bài bình lên thành công");
    } catch (error: any) {
      toast.error(extractErrorMessage(error, "Tải ảnh thất bại"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processUpload(file);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      await processUpload(file);
    }
  };

  const handleSave = async () => {
    if (!layout?.id) return;
    try {
      setIsSaving(true);
      await trpcClient.productionOrders.updatePrintLayoutImage.mutate({
        id: layout.id,
        fileUrl: fileUrl || "",
      });
      toast.success("Cập nhật hình ảnh bài bình thành công!");
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || "Không thể lưu hình ảnh bài bình");
    } finally {
      setIsSaving(false);
    }
  };

  if (!layout) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-5">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-md bg-indigo-50 text-indigo-600">
              <ImageIcon className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>
                Cập Nhật Ảnh Bài Bình Prepress
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {layout.name || "Bài bình in"} {layout.layoutCode ? `(${layout.layoutCode})` : ""}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-3">
          <div className="flex items-center justify-between text-xs text-slate-600 bg-slate-50 p-2.5 rounded border border-slate-100">
            <span>Khổ tờ in: <strong>{layout.sheetSize || "—"}</strong></span>
            <span>Số con/tờ: <strong>{layout.ups || 1} con</strong></span>
          </div>

          {/* Dropzone & Preview Box */}
          <div
            className={`relative min-h-[220px] rounded-lg border-2 border-dashed transition-all flex flex-col items-center justify-center p-4 overflow-hidden ${
              isDragging
                ? "border-indigo-500 bg-indigo-50/50"
                : fileUrl
                ? "border-slate-200 bg-slate-900/5"
                : "border-slate-200 bg-slate-50 hover:bg-slate-100/70"
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <div className="flex flex-col items-center gap-2 text-indigo-600">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-xs font-semibold">Đang xử lý &amp; nén ảnh...</span>
              </div>
            ) : fileUrl ? (
              <div className="relative w-full h-full flex items-center justify-center group">
                <img
                  src={getUploadUrl(fileUrl)}
                  alt="Bài bình"
                  className="max-h-[200px] w-auto object-contain rounded drop-shadow-sm transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 px-2.5 text-xs bg-white text-slate-800 hover:bg-slate-100 shadow"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="h-3.5 w-3.5 mr-1 text-indigo-600" /> Thay ảnh
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="h-8 px-2.5 text-xs shadow"
                    onClick={() => setFileUrl(null)}
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Gỡ ảnh
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-2 select-none">
                <div className="mx-auto w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                  <Upload className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-700">
                    Kéo thả file ảnh hoặc{" "}
                    <button
                      type="button"
                      className="text-indigo-600 hover:underline font-bold"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      chọn từ máy tính
                    </button>
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Hỗ trợ PNG, JPG, WebP, SVG
                  </p>
                </div>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-50/80 border border-indigo-100 text-[10px] text-indigo-700 font-medium mt-1">
                  <Clipboard className="h-3 w-3" /> Mẹo: Có thể dán trực tiếp (Ctrl + V)
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Direct URL input fallback */}
          <div className="space-y-1 pt-1">
            <label className="text-[11px] text-slate-500 font-semibold block">
              Hoặc nhập trực tiếp liên kết URL ảnh bản bình:
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="https://example.com/marquette.png"
                value={fileUrl || ""}
                onChange={(e) => setFileUrl(e.target.value)}
                className="h-8 text-xs font-mono flex-1"
              />
              {fileUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 px-2.5 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => setFileUrl(null)}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-2 border-t border-slate-100 flex items-center justify-between sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isUploading}
          >
            Hủy bỏ
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={handleSave}
            disabled={isSaving || isUploading || fileUrl === layout.fileUrl}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Đang lưu...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1.5" /> Lưu Thay Đổi
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
